// Central content for SX Heating LTD landing page
import {
  Flame, Wrench, Bath, ShieldAlert, Gauge, ShieldCheck,
  Handshake, Home, PoundSterling,
} from "lucide-react";

export const COMPANY = {
  name: "SX HEATING LTD",
  phone: "07861 385433",
  phoneIntl: "+447861385433",
  email: "sxheatingltd@gmail.com",
  companyNumber: "17361903",
  facebook: "https://www.facebook.com/SXHeatingLTD",
  reviewsUrl: "https://www.facebook.com/SXHeatingLTD/reviews",
  area: "All of Essex",
};

export const IMAGES = {
  heroBoiler:
    "https://images.pexels.com/photos/7859953/pexels-photo-7859953.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  engineer:
    "https://images.unsplash.com/photo-1732660513320-a6b489f3fece?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  boilerSwap:
    "https://images.unsplash.com/photo-1601914697928-0b536e76d048?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  systemConv:
    "https://images.pexels.com/photos/34938442/pexels-photo-34938442.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  bathroom:
    "https://images.unsplash.com/photo-1661107259637-4e1c55462428?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  bathroom2:
    "https://images.unsplash.com/photo-1638799869566-b17fa794c4de?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  breakdown:
    "https://images.unsplash.com/photo-1780034766226-6c2c62772512?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  service:
    "https://images.unsplash.com/photo-1711571603478-ff100499a459?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  radiator:
    "https://images.unsplash.com/photo-1518276779712-dfdcb9daa7a1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  underSink:
    "https://images.unsplash.com/photo-1676210133055-eab6ef033ce3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
};

export const SERVICES = [
  {
    id: "boiler-swaps",
    icon: Flame,
    title: "Boiler Swaps",
    blurb: "Upgrade to a more efficient boiler and save on your energy bills.",
    image: IMAGES.boilerSwap,
    points: ["A-rated combi & system boilers", "Free efficiency assessment", "Up to 10-year warranties"],
  },
  {
    id: "system-conversions",
    icon: Wrench,
    title: "System Conversions",
    blurb: "Convert your heating system to perfectly suit your home and needs.",
    image: IMAGES.systemConv,
    points: ["Conventional to combi", "Smart controls & zoning", "Full system flush"],
  },
  {
    id: "bathrooms",
    icon: Bath,
    title: "Bathrooms",
    blurb: "Beautiful, high-quality bathrooms designed and installed end to end.",
    image: IMAGES.bathroom,
    points: ["Design & supply", "Tiling & waterproofing", "Full project management"],
  },
  {
    id: "boiler-breakdowns",
    icon: ShieldAlert,
    title: "Boiler Break Downs",
    blurb: "Fast response to get your heating back up and running, day or night.",
    image: IMAGES.breakdown,
    points: ["Rapid fault diagnosis", "All makes & models", "24/7 emergency call-out"],
  },
  {
    id: "boiler-services",
    icon: Gauge,
    title: "Boiler Services",
    blurb: "Keep your boiler running safely and efficiently all year round.",
    image: IMAGES.service,
    points: ["Full annual service", "Gas Safe safety check", "Landlord certificates"],
  },
];

export const BADGES = [
  { icon: ShieldCheck, title: "Fully Qualified & Insured", text: "Gas Safe registered engineers, fully insured on every job." },
  { icon: Handshake, title: "Reliable & Trustworthy", text: "Turn up when we say, tidy up when we leave — every time." },
  { icon: Home, title: "Local, Friendly Service", text: "An Essex team that treats your home like our own." },
  { icon: PoundSterling, title: "Competitive Pricing", text: "Clear, upfront quotes with no hidden extras." },
];

export const MANIFESTO = [
  {
    n: "01",
    title: "Thermal precision",
    text: "Every install is heat-loss calculated and commissioned to manufacturer spec, so your system runs quiet, efficient and warm for years.",
    image: IMAGES.radiator,
  },
  {
    n: "02",
    title: "Local reliability",
    text: "We live and work across Essex. When you call, you get a local engineer who answers — not a national call centre.",
    image: IMAGES.engineer,
  },
  {
    n: "03",
    title: "Transparent pricing",
    text: "You get a clear written quote before we start. No surprise call-out fees, no padded invoices, no jargon.",
    image: IMAGES.underSink,
  },
  {
    n: "04",
    title: "Clean workmanship",
    text: "Dust sheets down, boots off, mess taken away. We leave your home exactly as we found it — only warmer.",
    image: IMAGES.bathroom2,
  },
];

// Real customer reviews from Google & Facebook.
export const REVIEWS = [
  { name: "Alex Moore", location: "Google · Local Guide", rating: 5, text: "Louie & Roy were amazing; they came and quoted my bathroom work, including a full system change from an old unvented system. They were true professionals from start to finish. I loved the father-and-son dynamic and how they both bring their own strengths to the project. They went above and beyond — little things like making sure I wasn't without a toilet whilst the work was ongoing meant a lot. I'd very happily recommend them to anyone who needs brilliant plumbers!" },
  { name: "Russ Chapman", location: "Google Review", rating: 5, text: "Roy and Louie were very knowledgeable and did a great job and got me out of a fix when my boiler broke down. Would highly recommend." },
  { name: "Michael Chapman", location: "Facebook · Recommends", rating: 5, text: "Roy is one of the best plumbers around. He knows his stuff, is polite, and also very clean. He takes pride in his work and has a customer-first approach. Will always go to Roy first in the future." },
  { name: "Daren Clark", location: "Facebook · Recommends", rating: 5, text: "I've used this company for myself, my son and my daughter and the service has been excellent. Roy is very friendly and helpful and works until the job is finished — none of this 'come back tomorrow' stuff. I thoroughly recommend you give them a try." },
];

export const POSTCODES = [
  "Chelmsford", "Colchester", "Southend-on-Sea", "Basildon", "Harlow",
  "Brentwood", "Braintree", "Maldon", "Witham", "Billericay",
  "Wickford", "Rayleigh", "Clacton-on-Sea", "Epping", "Ongar",
];
